import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useTakeoff } from "./store";
import { INPUT_SM, MONO } from "./theme";
import { money, parseSizeThickness, PVC_GRID, ALUMINUM_X_PVC } from "./pricing";
import { LibraryItemDialog, PipeBuilderDialog, DuctBuilderDialog, ConfirmDialog } from "./dialogs";

// '1-1/4" Cold Water 1" INS' -> system "Cold Water" (size/thickness come from
// parseSizeThickness). System is matched on its own — a 1" pipe is not the
// same as 1" insulation.
const SYSTEM_RE = /^[\d./-]+"\s+(.+?)\s+[\d.]+"\s+INS\s*$/i;
const pipeSystem = (name) => {
  const m = SYSTEM_RE.exec((name || "").replace(/[“”]/g, '"').trim());
  return m ? m[1].trim() : null;
};
const FILTER_KEY = "takeoff-library-pipe-filters";
const loadFilters = () => {
  try { return JSON.parse(localStorage.getItem(FILTER_KEY)) || {}; } catch { return {}; }
};

/**
 * Company Library — the shared priced catalogue. Built-ins can't be
 * deleted; custom items can be created/edited/deleted. Plus adds a snapshot
 * to the current job; a check means it's already in the job (click removes
 * it from THIS job only — measured geometry survives). Pipe and duct items
 * are built with the same structured builders the job palette uses.
 */
export default function LibraryPanel() {
  const { library, jobItems, addJobItem, removeJobItem, addLibraryItem, updateLibraryItem, removeLibraryItem, activeId, viewer } = useTakeoff();
  const [cat, setCat] = useState("Pipe");
  const [q, setQ] = useState("");
  const [sys, setSysState] = useState(() => loadFilters().sys || "");
  const [thick, setThickState] = useState(() => loadFilters().thick || "");
  const [newOpen, setNewOpen] = useState(false);
  const [pipeOpen, setPipeOpen] = useState(false);
  const [ductOpen, setDuctOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const persistFilters = (s, t) => {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify({ sys: s, thick: t })); } catch { /* private mode */ }
  };
  const setSys = (v) => { setSysState(v); persistFilters(v, thick); };
  const setThick = (v) => { setThickState(v); persistFilters(sys, v); };

  const systems = useMemo(() => [...new Set(
    library.filter((i) => i.category === "Pipe").map((i) => pipeSystem(i.name)).filter(Boolean),
  )].sort(), [library]);
  const thicknesses = useMemo(() => [...new Set(
    library.filter((i) => i.category === "Pipe").map((i) => parseSizeThickness(i.name)[1]).filter(Boolean),
  )].sort((a, b) => parseFloat(a) - parseFloat(b)), [library]);

  const list = useMemo(() => {
    let l = library.filter((i) => i.category === cat);
    if (cat === "Pipe") {
      if (sys) l = l.filter((i) => pipeSystem(i.name) === sys);
      if (thick) l = l.filter((i) => parseSizeThickness(i.name)[1] === thick);
    }
    if (q.trim()) l = l.filter((i) => i.name.toLowerCase().includes(q.trim().toLowerCase()));
    return [...l].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [library, cat, q, sys, thick]);

  const jobItemFor = (libId) => jobItems.find((i) => i.library_id === libId);

  const addToJob = async (item) => {
    if (!activeId) { toast.message("Pick a job first"); return; }
    await addJobItem({
      name: item.name, category: item.category, tool: item.tool, unit: item.unit,
      rate: item.rate, color: item.color, lineWidth: 4, libraryId: item.item_id,
      ...(item.ductSpec ? { ductSpec: item.ductSpec } : {}),
    });
    toast.success(`${item.name} added to this job`);
  };

  return (
    <div className="flex h-full flex-col p-3" data-testid="library-panel">
      <div className="flex items-center gap-1">
        {["Pipe", "Duct", "Count"].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`library-cat-${c}`}
            aria-pressed={cat === c}
            className={`flex-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${cat === c ? "bg-[#0b172a] text-white" : "border border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            {c}
          </button>
        ))}
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cat.toLowerCase()} items`} className={`${INPUT_SM} mt-2`} data-testid="library-search" />
      {cat === "Pipe" && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <select value={sys} onChange={(e) => setSys(e.target.value)} className={`${INPUT_SM} flex-1`} data-testid="library-filter-system" aria-label="Filter by system">
            <option value="">All systems</option>
            {systems.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={thick} onChange={(e) => setThick(e.target.value)} className={`${INPUT_SM} w-[110px]`} data-testid="library-filter-thickness" aria-label="Filter by insulation thickness">
            <option value="">All insulation</option>
            {thicknesses.map((t) => <option key={t} value={t}>{t} insulation</option>)}
          </select>
          {(sys || thick || q) && (
            <button onClick={() => { setSys(""); setThick(""); setQ(""); }} aria-label="Clear filters" title="Clear filters"
              className="rounded-md border border-slate-200 p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600" data-testid="library-filter-reset">
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {!viewer && (
      <div className="mt-2 flex gap-1.5">
        <button onClick={() => setPipeOpen(true)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="library-new-pipe"><Plus className="mr-1 inline h-3 w-3" />New pipe</button>
        <button onClick={() => setDuctOpen(true)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="library-new-duct"><Plus className="mr-1 inline h-3 w-3" />New duct</button>
        <button onClick={() => setNewOpen(true)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50" data-testid="library-new-item"><Plus className="mr-1 inline h-3 w-3" />New item</button>
      </div>
      )}

      <div className="mt-2 min-h-0 flex-1 space-y-1 overflow-y-auto">
        {cat === "Pipe" && !viewer && list.length > 0 && (
          <p className={`${MONO} px-2 text-[9px] uppercase tracking-widest text-slate-500`} data-testid="library-pipe-price-legend">
            per ft: base · <span className="text-slate-700">PVC</span> · <span className="text-[#b45309]">Alum = {ALUMINUM_X_PVC}× PVC</span>
          </p>
        )}
        {list.map((it) => {
          const inJob = jobItemFor(it.item_id);
          // pipe jackets: PVC grid price for this size + thickness, aluminum = 3 × PVC
          const [pSize, pThick] = it.category === "Pipe" && it.tool !== "count" ? parseSizeThickness(it.name) : [null, null];
          const pvc = pSize && pThick ? PVC_GRID[pSize]?.[pThick] : undefined;
          return (
            <div key={it.item_id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-2 py-1.5 hover:border-slate-200" data-testid={`library-item-${it.item_id}`}>
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: it.color }} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-slate-800">{it.name}</p>
                <p className={`${MONO} text-[10px] text-slate-400`}>
                  {viewer ? `per ${it.unit}${it.tool !== "count" ? " · 4 px" : ""}` : <>{money(it.rate)}/{it.unit}{it.tool !== "count" ? " · 4 px" : ""}{it.builtin ? " · built-in" : ""}</>}
                </p>
                {!viewer && pvc != null && (
                  <p className={`${MONO} text-[10px]`} data-testid={`library-jacket-${it.item_id}`}>
                    <span className="text-slate-700">PVC {money(pvc)}</span>
                    <span className="text-slate-300"> · </span>
                    <span className="font-semibold text-[#b45309]">Alum {money(Math.round(pvc * ALUMINUM_X_PVC * 100) / 100)}</span>
                    <span className="text-slate-400">/ft</span>
                  </p>
                )}
              </div>
              {!viewer && (
              <button
                aria-label={inJob ? `Remove ${it.name} from this job` : `Add ${it.name} to this job`}
                title={inJob ? "In this job — click to remove from this job" : "Add to this job"}
                onClick={() => (inJob ? removeJobItem(inJob.item_id) : addToJob(it))}
                data-testid={`library-toggle-${it.item_id}`}
                className={`rounded-md p-1.5 transition-colors ${inJob ? "bg-emerald-100 text-emerald-700 hover:bg-red-50 hover:text-red-500" : "bg-slate-100 text-slate-500 hover:bg-[#f97316] hover:text-white"}`}>
                {inJob ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              </button>
              )}
              {!viewer && !it.builtin && (
                <>
                  <button aria-label={`Edit ${it.name}`} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" onClick={() => setEditItem(it)} data-testid={`library-edit-${it.item_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button aria-label={`Delete ${it.name}`} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500" onClick={() => setDelItem(it)} data-testid={`library-delete-${it.item_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </>
              )}
              {!viewer && it.builtin && (it.category === "Count") && (
                <button aria-label={`Edit price of ${it.name}`} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600" onClick={() => setEditItem(it)} data-testid={`library-edit-${it.item_id}`}><Pencil className="h-3.5 w-3.5" /></button>
              )}
            </div>
          );
        })}
        {!list.length && <p className="py-6 text-center text-[11px] text-slate-400">No items match.</p>}
      </div>

      <LibraryItemDialog open={newOpen} onClose={() => setNewOpen(false)} onSave={(item) => addLibraryItem(item)} />
      <PipeBuilderDialog mode="library" open={pipeOpen} onClose={() => setPipeOpen(false)} library={library} onSave={(item) => addLibraryItem(item)} />
      <DuctBuilderDialog open={ductOpen} onClose={() => setDuctOpen(false)} onSave={(item) => addLibraryItem(item)} />
      {/* edits update the existing library item in place — never duplicate */}
      <PipeBuilderDialog mode="library" open={!!editItem && editItem.category === "Pipe" && !!parseSizeThickness(editItem.name)[0]}
        onClose={() => setEditItem(null)} library={library} editItem={editItem} onSave={(p) => updateLibraryItem(editItem.item_id, p)} />
      <DuctBuilderDialog open={!!editItem && !!editItem.ductSpec} onClose={() => setEditItem(null)}
        editItem={editItem} onSave={(p) => updateLibraryItem(editItem.item_id, p)} />
      <LibraryItemDialog open={!!editItem && !editItem.ductSpec && !parseSizeThickness(editItem?.name || "")[0]} onClose={() => setEditItem(null)} item={editItem} onSave={(p) => updateLibraryItem(editItem.item_id, p)} />
      <ConfirmDialog open={!!delItem} onClose={() => setDelItem(null)}
        title={`Delete ${delItem?.name}?`}
        body="The item leaves the company library and every job's palette. Takeoffs already measured with it stay on the drawings."
        onConfirm={() => removeLibraryItem(delItem.item_id)} />
    </div>
  );
}
